import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Box, Typography, Tabs, TextField, Button, Spinner, Alert, Chip, EmptyState } from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import {
  adminApiClient,
  AdminApiError,
  type ReviewProduct,
  type VendorProductReviewItem,
  type VendorProductReviewState,
  type VendorProductSent,
} from '../lib/adminApiClient';
import { DANGER_BTN } from '../lib/buttonStyles';
import { Actions, Card, Field, MUTED, Panel, Photo, PRIMARY_BTN, SideBySide, day, money } from './vendorReview/parts';
import { NOTE_MAX, STATE_LABEL, errorMessage, itemTitle, kindLabel, noteProblem, readState, sentBarcode } from './vendorProducts/viewModel';

/**
 * Every new product a vendor adds waits here until a staff member approves it.
 * A product matched to one of ours shows what the vendor sent beside our record.
 */
export default function VendorProducts() {
  const caps = useCapabilities();
  const canView = caps.has('vendor:read');
  const canReview = caps.has('vendor:review');
  const [params, setParams] = useSearchParams();
  const state = readState(params.get('state'));
  const [items, setItems] = useState<VendorProductReviewItem[] | null>(null);
  const [counts, setCounts] = useState<{ pending: number; returned: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const load = useCallback(async (which: VendorProductReviewState) => {
    setError(null);
    setItems(null);
    try {
      const res = await adminApiClient.listVendorProductReview({ state: which, limit: 200 });
      setItems(res.items);
      setCounts(res.counts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load new products');
    }
  }, []);

  useEffect(() => {
    if (canView) void load(state);
  }, [canView, load, state]);

  if (caps.loading)
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Spinner color="brand" />
      </Box>
    );
  if (!canView) return <Alert severity="error">You don’t have permission to view new vendor products.</Alert>;

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
        New products
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Products vendors have added. Shoppers see one only after it is approved here.
      </Typography>
      {!canReview && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="info">View-only access — you can read the queue but not decide.</Alert>
        </Box>
      )}
      <Box sx={{ mb: 2 }}>
        <Tabs
          ariaLabel="New products queue"
          value={state}
          onChange={(_e, v) => {
            setFlash(null);
            setParams({ state: String(v) });
          }}
          tabs={(['pending', 'returned'] as VendorProductReviewState[]).map((s) => ({
            value: s,
            label: STATE_LABEL[s],
            badgeCount: counts?.[s],
          }))}
        />
      </Box>
      {flash && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="success">{flash}</Alert>
        </Box>
      )}
      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : !items ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <Spinner color="brand" />
        </Box>
      ) : items.length === 0 ? (
        <EmptyState
          icon="listAlt"
          title={state === 'pending' ? 'No new products waiting' : 'Nothing sent back'}
          body={state === 'pending' ? 'A product a vendor adds shows here until someone approves it.' : 'Products sent back to a vendor with a note show here.'}
        />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {items.map((item) => (
            <ProductCard
              key={item.vendorProductId}
              item={item}
              canReview={canReview && state === 'pending'}
              onDone={(msg) => {
                setFlash(msg);
                void load(state);
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

function SentColumn({ sent }: { sent: VendorProductSent | null }) {
  if (!sent) return <Typography sx={{ fontSize: 14, color: MUTED }}>Nothing on record from the vendor.</Typography>;
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
        <Photo src={sent.photoUrl} alt={`The vendor’s photo of ${sent.name ?? 'the product'}`} size={140} />
        {sent.nutritionLabelUrl && <Photo src={sent.nutritionLabelUrl} alt="The vendor’s nutrition label photo" size={140} />}
      </Box>
      <Field label="Name">{sent.name}</Field>
      <Field label="Size">{sent.size}</Field>
      <Field label="Department">{[sent.department, sent.category].filter(Boolean).join(' › ')}</Field>
      <Field label="Short description">{sent.descriptionShort}</Field>
      <Field label="Ingredients">{sent.ingredients}</Field>
      <Field label="Barcode" testId="vendor-product-sent-barcode">{sentBarcode(sent)}</Field>
      <Field label="Vendor SKU">{sent.vendorSku}</Field>
      <Field label="Price">{sent.priceCents == null ? null : money(sent.priceCents)}</Field>
      <Field label="Came from">{[sent.source, sent.file].filter(Boolean).join(' · ')}</Field>
    </Box>
  );
}

function OurColumn({ product }: { product: ReviewProduct }) {
  return (
    <Box>
      <Box sx={{ mb: 1.5 }}>
        <Photo src={product.imageUrl} alt={`Our photo of ${product.name}`} size={140} />
      </Box>
      <Field label="Name">{[product.brand, product.name].filter(Boolean).join(' — ')}</Field>
      <Field label="Size">{product.size}</Field>
      <Field label="Department">{[product.department, product.category].filter(Boolean).join(' › ')}</Field>
      <Field label="Short description">{product.descriptionShort}</Field>
      <Field label="Ingredients">{product.ingredients}</Field>
      <Field label="Barcode ends in" testId="vendor-product-our-barcode">{product.barcodeLast4}</Field>
      <Field label="Product ID">{product.glpProductId}</Field>
    </Box>
  );
}

function ProductCard({ item, canReview, onDone }: { item: VendorProductReviewItem; canReview: boolean; onDone: (msg: string) => void }) {
  const [returning, setReturning] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const title = itemTitle(item);
  const problem = noteProblem(note);

  const run = async (work: () => Promise<unknown>, msg: string) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      onDone(msg);
    } catch (err) {
      setError(err instanceof AdminApiError ? errorMessage(err.code, err.message) : 'That did not go through');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card testId={`vendor-product-${item.vendorProductId}`}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography component="h2" sx={{ fontSize: 18, fontWeight: 700 }}>
          {title}
        </Typography>
        <Chip label={item.vendor.businessName} size="small" />
        <Chip label={kindLabel(item.kind)} size="small" color={item.kind === 'matched' ? 'default' : 'warning'} />
      </Box>
      <Typography sx={{ fontSize: 13, color: MUTED }}>
        Added {day(item.addedAt)}
        {item.vendor.glpVendorId ? ` · ${item.vendor.glpVendorId}` : ''}
      </Typography>
      {item.note && (
        <Box sx={{ mt: 1.5 }}>
          <Field label="Our note to the vendor">{item.note}</Field>
        </Box>
      )}

      {item.kind === 'matched' ? (
        <SideBySide leftTitle="What the vendor sent" rightTitle="Our product" left={<SentColumn sent={item.sent} />} right={<OurColumn product={item.product} />} />
      ) : (
        <Box sx={{ mt: 2, border: '1px solid #e2e8f0', borderRadius: '8px', p: 2 }}>
          <Typography component="h3" sx={{ fontSize: 14, fontWeight: 700, mb: 1.5 }}>
            What the vendor sent
          </Typography>
          <SentColumn sent={item.sent} />
        </Box>
      )}

      {canReview && (
        <Actions>
          <Button
            ariaLabel={`Approve ${title}`}
            buttonText={busy && !returning ? 'Approving…' : 'Approve'}
            variant="contained"
            size="small"
            sx={PRIMARY_BTN}
            disabled={busy}
            dataTestId="vendor-product-approve"
            onClick={() => void run(() => adminApiClient.approveVendorProduct(item.vendorProductId), `Approved. Shoppers can now see ${title}.`)}
          />
          <Button
            ariaLabel={`Send ${title} back to ${item.vendor.businessName}`}
            buttonText="Send back"
            variant="outlined"
            size="small"
            sx={DANGER_BTN}
            disabled={busy}
            dataTestId="vendor-product-return"
            onClick={() => setReturning((v) => !v)}
          />
        </Actions>
      )}
      {canReview && returning && (
        <Panel title="What should the vendor fix?">
          <TextField
            label={`${item.vendor.businessName} reads this`}
            ariaLabel="Note to the vendor"
            multiline
            minRows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            dataTestId="vendor-product-return-note"
          />
          <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.5 }}>
            {note.trim().length}/{NOTE_MAX}
          </Typography>
          <Box sx={{ mt: 1.5 }}>
            <Button
              ariaLabel="Send it back"
              buttonText={busy ? 'Sending…' : 'Send back'}
              variant="contained"
              size="small"
              sx={PRIMARY_BTN}
              disabled={busy || problem !== null}
              dataTestId="vendor-product-return-go"
              onClick={() => void run(() => adminApiClient.returnVendorProduct(item.vendorProductId, note.trim()), `Sent back to ${item.vendor.businessName} with your note.`)}
            />
          </Box>
        </Panel>
      )}
      {error && (
        <Box sx={{ mt: 1.5 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
    </Card>
  );
}
